import Head from 'next/head'
import Link from 'next/link'
import { useState } from 'react'
import ButtonLink from '../components/ButtonLink/ButtonLink'
import ContactForm from '../components/ContactForm/ContactForm'
import FormSuccessDialog from '../components/FormSuccessDialog/FormSuccessDialog'
import CompareTable from '../components/Table/CompareTable'
import JsonLd from '../components/SEO/JsonLd'
import SEO from '../components/SEO/SEO'
import Layout from '../layout/Layout'

import classes from '../styles/section.module.css'

export default function Home() {
  const [getStartedValue, setGetStartedValue] = useState('individual')
  const [showModal, setShowModal] = useState(false)

  const handleGetStarted = e => {
    e.preventDefault()
    setShowModal(true)
  }

  const toggleModal = () => {
    setShowModal(false)
  }
  return (
    <>
      <SEO slug="" />
      <Head>
        <JsonLd data={{
          '@context': 'https://schema.org',
          '@type': 'InsuranceAgency',
          name: 'Evry Health',
          url: 'https://www.evryhealth.com',
          telephone: '+1-855-579-3879',
          areaServed: { '@type': 'State', name: 'Texas' },
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Dallas',
            addressRegion: 'TX',
            addressCountry: 'US',
          },
        }} />
      </Head>
      <Layout>
        <main>
          {/* Hero card */}
          <div>
            <div className="relative">
              {showModal && (
                <FormSuccessDialog
                  userType="individual"
                  toggleModal={toggleModal}
                />
              )}
              <div className="absolute inset-x-0 bottom-0 h-1/2" />
              <div className="mx-auto">
                <div className="relative">
                  <div className="absolute inset-0">
                    <img
                      className="object-cover w-full h-full"
                      src="homepage-hero-background.png"
                      alt="People working on laptops"
                    />
                  </div>
                  <div className="relative max-w-screen-xl px-8 py-16 mx-auto md:py-16 lg:py-32">
                    <h1
                      className="text-4xl tracking-tight text-blue-dark md:text-5xl md:max-w-[800px]"
                      style={{ lineHeight: '1.2em' }}
                    >
                      Evry is bringing humanity back to health insurance.
                    </h1>
                    <p className="max-w-lg mt-6 text-xl text-grey sm:max-w-3xl">
                      We bring a personalized, human approach to how{' '}
                      <span className="inline md:block">
                        we deliver health insurance.
                      </span>
                    </p>
                    <div className="hidden max-w-sm mx-auto mt-10 sm:max-w-none md:flex">
                      <p className="inline-block px-8 py-4 mb-2 text-lg border-l-2 border-r-2 bg-white-light text-blue md:mb-0 border-y-2 md:border-r-0 border-blue">
                        You are a...
                      </p>
                      <select
                        className="px-8 py-4 text-lg bg-white-light text-blue border-2 border-blue inline-block font-[sans-serif]"
                        onChange={e => {
                          setGetStartedValue(e.target.value)
                        }}
                      >
                        <option value="employer">Employer (51-499)</option>
                        <option value="employer">Employer (500+)</option>
                        <option value="employer">Broker</option>
                        <option value="individual">Individual</option>
                      </select>
                      {getStartedValue === 'individual' ? (
                        <button
                          className="inline-block px-8 py-4 mt-2 text-lg border-2 md:mt-0 bg-blue text-white-light border-blue"
                          onClick={handleGetStarted}
                        >
                          Get Started
                        </button>
                      ) : (
                        <Link href="/contact-us">
                          <button className="inline-block px-8 py-4 mt-2 text-lg border-2 md:mt-0 bg-blue text-white-light border-blue">
                            Get Started
                          </button>
                        </Link>
                      )}
                    </div>
                    <div className="block md:hidden">
                      <Link href="/contact-us">
                        <button className="inline-block px-8 py-4 mt-8 text-xl border-2 md:mt-0 bg-blue text-white-light border-blue">
                          Get Started
                        </button>
                      </Link>
                    </div>
                    <p className="block pt-2 pr-16 text-xl text-grey md:text-base md:pr-0">
                      <small>
                        Available only for employers with 51 or more employees
                        in Texas.
                      </small>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Hero card */}

          {/* Benefits Info */}
          <section>
            <div className="max-w-screen-xl px-4 py-12 mx-auto sm:px-6 lg:px-8">
              <div className="lg:grid lg:grid-cols-2 lg:gap-zf8 lg:items-center">
                <div className="lg:col-span-1">
                  <h2 className="text-4xl tracking-tight font-extralight">
                    <span className="block text-blue-dark">
                      Better Health Insurance for
                    </span>
                    <span className="block text-blue">
                      Your Employees and Business.
                    </span>
                  </h2>
                  <p className="max-w-lg mt-6 text-lg text-grey sm:max-w-3xl">
                    Healthcare is complicated and expensive. We are making
                    <span className="block">
                      {' '}
                      it simple and accessible. What does that mean for you?
                    </span>
                  </p>
                  <ul className="text-lg text-grey">
                    <li className="mt-4">
                      <object
                        className="inline-block h-6"
                        type="image/svg+xml"
                        data="checkmark.svg"
                      ></object>
                      <p className="inline pl-4">
                        No copays. No deductibles. Really.
                      </p>
                    </li>
                    <li className="mt-4">
                      <object
                        className="inline-block h-6"
                        type="image/svg+xml"
                        data="checkmark.svg"
                      ></object>
                      <p className="inline pl-4">
                        Premiums built with your financial well-being in mind
                      </p>
                    </li>
                    <li className="mt-4">
                      <object
                        className="inline-block h-6"
                        type="image/svg+xml"
                        data="checkmark.svg"
                      ></object>
                      <p className="inline pl-4">
                        Free mental health visits (you deserve it!)
                      </p>
                    </li>
                    <li className="mt-4">
                      <object
                        className="inline-block h-6"
                        type="image/svg+xml"
                        data="checkmark.svg"
                      ></object>
                      <p className="inline pl-4">And much, much more</p>
                    </li>
                  </ul>
                  <ButtonLink uri="/contact-us">Get Started</ButtonLink>
                </div>
                <div className="lg:col-span-1">
                  <div className="flex items-center justify-center h-full">
                    <figure className={`${classes.sectionImageRight}`}>
                      <img
                        className="object-cover w-full h-full mt-4 md:mt-0"
                        src="friends-looking-at-sunset.png"
                        alt="friends looking at sunset"
                      />
                    </figure>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="max-w-screen-xl px-4 py-12 mx-auto sm:px-6 lg:px-8">
              <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
                <div className="lg:col-span-1">
                  <div className="flex items-center justify-center">
                    <object
                      className="h-56 mb-10 md:h-96"
                      type="image/svg+xml"
                      data="homepage-app-flow.svg"
                    ></object>
                  </div>
                </div>

                <div className="lg:col-span-1">
                  <h2 className="text-4xl tracking-tight font-extralight">
                    <span className="block text-blue-dark">
                      Doctors when you need them.
                    </span>
                    <span className="block text-blue">For $0.</span>
                  </h2>
                  <p className="max-w-lg mt-6 text-lg text-grey sm:max-w-3xl">
                    Your team will love our team!
                    <span className="block mt-6">
                      Our network of primary care doctors, specialists,
                      nutritionists, and therapists who are available within
                      moments virtually without paying anything out of pocket.
                    </span>
                    <span className="block mt-6">
                      Personalized care plans with a team of medical
                      professionals who work around the clock to help meet
                      health goals, save money, and access the care your
                      employees need.
                    </span>
                  </p>
                  <ButtonLink uri="/meet-your-new-team">
                    Meet your new care team
                  </ButtonLink>
                </div>
              </div>
            </div>
          </section>
          {/* Benefits Info */}

          <section>
            <div className="bg-white">
              <div className="max-w-screen-xl px-6 py-12 mx-auto md:px-8">
                <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
                  <div className="lg:col-span-1">
                    <h2 className="text-4xl tracking-tight font-extralight">
                      <span className="block text-blue-dark">
                        Life is unpredictable.
                      </span>
                      <span className="block text-green">
                        Health insurance shouldn&#39;t be.
                      </span>
                    </h2>
                    <p className="max-w-lg mt-6 text-xl text-grey sm:max-w-3xl">
                      Evry Health is eliminating confusion and frustration over{' '}
                      <span className="text-grey">
                        healthcare costs and coverage. Simple. Clear.
                        Dependable.
                      </span>
                    </p>
                    <ul className="text-grey">
                      <li className="mt-4">
                        <object
                          className="inline-block h-6"
                          type="image/svg+xml"
                          data="checkmark.svg"
                        ></object>
                        <p className="inline-block pl-4">
                          Customized care at affordable premiums
                        </p>
                      </li>
                      <li className="mt-4">
                        <object
                          className="inline-block h-6"
                          type="image/svg+xml"
                          data="checkmark.svg"
                        ></object>
                        <p className="inline-block pl-4">
                          Free mental health visits
                        </p>
                      </li>
                      <li className="mt-4">
                        <object
                          className="inline-block h-6"
                          type="image/svg+xml"
                          data="checkmark.svg"
                        ></object>
                        <p className="inline-block pl-4">
                          Employee rewards and more
                        </p>
                      </li>
                    </ul>
                  </div>
                  <div className="lg:col-span-1">
                    <div className="flex items-center justify-center h-full mt-10 md:mt-0">
                      <img
                        className={`${classes.homepagePhoneOne} object-cover pl-2 md:pl-16`}
                        src="homepage-phone-1.png"
                        alt="Phone with page for looking at coverage"
                      />
                      <img
                        className={`${classes.homepagePhoneTwo} object-cover pl-2 md:pl-16`}
                        src="homepage-phone-2.png"
                        alt="Phone with my profile"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            {/* table with comparing two companies */}
            <div className="max-w-screen-xl px-4 py-12 mx-auto md:py-32 sm:px-6 lg:px-8">
              <div className="pb-12 text-center md:pt-32">
                <h2 className="pb-6 text-4xl font-light tracking-tight">
                  Deductibles? <span className="text-globeGreen">Gone.</span>{' '}
                  Copays? <span className="text-globeGreen">Gone.</span>
                </h2>
                <h3 className="text-grey font-[sans-serif]">
                  Our plans give your employees the care they want when they
                  need it.
                </h3>
              </div>
              <CompareTable />

              <section>
                <ContactForm />
              </section>
            </div>
          </section>

          <section className="bg-white">
            <div className="px-4 py-24 mx-auto max-w-screen-full sm:px-6 lg:px-8">
              <div className="text-center">
                <h3 className="text-2xl font-extralight tracking-tight sm:text-3xl lg:text-3xl pb-4 font-[Lora]">
                  Featured on
                </h3>
              </div>
              <div className="text-center">
                <img
                  className="inline-block mx-10"
                  src="https://content.evryhealth.com/MarketingImages/public%20(1).webp"
                  alt="Dallas Innovates Logo"
                />
                <img
                  className="inline-block mx-10 max-h-24"
                  src="https://content.evryhealth.com/MarketingImages/public%20(2).webp"
                  alt="D Magazine Logo"
                />
                <img
                  className="inline-block mx-10 max-h-32"
                  src="https://content.evryhealth.com/MarketingImages/public%20(3).webp"
                  alt="Built In Logo"
                />
                <object
                  className="inline-block h-48 mx-10"
                  type="image/svg+xml"
                  data="business-wire-logo-square.svg"
                ></object>
              </div>
            </div>
          </section>
        </main>
      </Layout>
    </>
  )
}
